import styled, { css } from "styled-components";
import { typography } from "screens/Homepage/components/typography";
import { SectionContainer, SectionTitle, LinkMarketingButton } from "screens/Homepage/styles";
import { Icon } from "@beamcloud/design-system";
import Magellan from "assets/magellan.svg";
import GeoSpy from "assets/geospy.svg";

import { ROUTES } from "routes/constants";
import { useState, useRef } from "react";

const CaseStudiesSection: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const caseStudies = [
    {
      company: "MAGELLAN AI",
      description:
        "I love Beam. You don't even need to configure anything. You just push to their Github hook, and all of a sudden you have an app running in the cloud.",
      author: "Ryan Kerr",
      role: "Senior Engineer at Magellan AI",
      link: "https://beam.cloud/customers/magellan-ai-case-study",
      logo: Magellan,
      avatar:
        "https://cdn.sanity.io/images/eb1odxxp/customers/0d3c5b863604381f419d7b113eaa22a767900b71-450x450.jpg",
    },
    {
      company: "GEOSPY",
      description:
        "We would not have been able to get our API running and win our first enterprise contract without Beam.",
      author: "Daniel Heinen",
      role: "CEO @ Graylark Technologies and Geospy",
      link: "https://beam.cloud/customers/geospy-case-study",
      logo: GeoSpy,
      avatar:
        "https://cdn.sanity.io/images/eb1odxxp/customers/737203eaba5e4bbb078a0377ce459f8ca04f4a03-800x800.png",
    },
    {
      company: "Hooktheory",
      description:
        "Beam’s infrastructure makes productizing our AI straightforward. We can scale up or down when needed, test new GPU options, and keep our users happy.",
      author: "Dave Carlton",
      role: "Founder @ Hooktheory",
      link: "https://beam.cloud/customers/hooktheory-case-study",
      logo: null,
      avatar:
        "https://cdn.sanity.io/images/eb1odxxp/customers/5accb2ed310be1230abf678afc9057e012ddedeb-2699x2649.jpg",
    },
  ];

  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % caseStudies.length);
  };

  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + caseStudies.length) % caseStudies.length);
  };

  return (
    <Container>
      <HeaderSection>
        <TitleWrapper>
          <StyledTitle>We help the best AI companies succeed</StyledTitle>
        </TitleWrapper>
        <ButtonGroup>
          <LinkMarketingButton href={ROUTES.customers} buttonTheme="primary">
            Explore case studies
          </LinkMarketingButton>
        </ButtonGroup>
      </HeaderSection>

      <CarouselContainer>
        <CarouselTrack
          ref={carouselRef}
          style={{
            transform: `translateX(-${currentIndex * (100 / caseStudies.length)}%)`,
          }}
        >
          {caseStudies.map((study, index) => (
            <CaseStudyLink key={index} href={study.link} target="_blank" rel="noopener noreferrer">
              <CaseStudyCard $isFeatured={index === currentIndex}>
                <LogoAndAvatarContainer>
                  {study.logo && (
                    <LogoImage>
                      <img src={study.logo} alt={study.company} />
                    </LogoImage>
                  )}
                  <AvatarImage>
                    <img src={study.avatar} alt={study.author} />
                  </AvatarImage>
                </LogoAndAvatarContainer>
                <Description>{study.description}</Description>
                {study.author && study.role && (
                  <AuthorInfo>
                    <AuthorName>{study.author}</AuthorName>
                    <AuthorRole>{study.role}</AuthorRole>
                  </AuthorInfo>
                )}
              </CaseStudyCard>
            </CaseStudyLink>
          ))}
        </CarouselTrack>
      </CarouselContainer>
      <NavigationButtons>
        <NavButton onClick={prevSlide}>
          <Icon icon="far-angle-left" size={16} />
        </NavButton>
        <NavButton onClick={nextSlide}>
          <Icon icon="far-angle-right" size={16} />
        </NavButton>
      </NavigationButtons>
    </Container>
  );
};

export default CaseStudiesSection;

const Container = styled(SectionContainer)`
  ${({ theme }) => {
    return css`
      padding: 80px 16px 160px;
      gap: 64px;

      @media (min-width: ${theme.breakpoints.sm}px) {
        padding: 96px 32px 192px;
        gap: 80px;
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        padding: 96px 48px 192px;
        gap: 96px;
      }

      @media (min-width: ${theme.breakpoints.lg}px) {
        padding: 96px 32px 192px;
      }

      @media (min-width: ${theme.breakpoints.xl}px) {
        padding: 96px 64px;
      }
    `;
  }}
`;

const HeaderSection = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: 32px;
      width: 100%;
      max-width: 1200px;
      text-align: left;

      @media (min-width: ${theme.breakpoints.md}px) {
        align-items: center;
        text-align: center;
        gap: 48px;
      }
    `;
  }}
`;

const TitleWrapper = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;

      @media (min-width: ${theme.breakpoints.md}px) {
        align-items: center;
        gap: 24px;
      }
    `;
  }}
`;

const StyledTitle = styled(SectionTitle)`
  ${({ theme }) => {
    return css`
      max-width: 100%;
      text-align: left;

      @media (min-width: ${theme.breakpoints.md}px) {
        text-align: center;
      }
    `;
  }}
`;

const ButtonGroup = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      justify-content: flex-start;
      gap: 12px;

      @media (max-width: ${theme.breakpoints.sm}px) {
        flex-direction: column;
        width: 100%;
        gap: 8px;
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        justify-content: center;
        gap: 16px;
      }
    `;
  }}
`;

const CarouselContainer = styled.div`
  ${({ theme }) => {
    return css`
      position: relative;
      width: 100%;
      max-width: 1200px;
      overflow: hidden;
    `;
  }}
`;

const CarouselTrack = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      gap: 24px;
      transition: transform 0.3s ease;
      width: ${3 * 100}%;

      @media (max-width: ${theme.breakpoints.md}px) {
        width: 100%;
        flex-direction: column;
        gap: 16px;
        transform: none !important;
      }
    `;
  }}
`;

const CaseStudyLink = styled.a`
  ${({ theme }) => {
    return css`
      text-decoration: none;
      color: inherit;
      display: block;
      width: calc(33.333% - 16px);

      @media (max-width: ${theme.breakpoints.md}px) {
        width: 100%;
      }
    `;
  }}
`;

const CaseStudyCard = styled.div<{ $isFeatured?: boolean }>`
  ${({ theme, $isFeatured }) => {
    return css`
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      padding: 32px 24px;
      background: ${$isFeatured ? theme.colors.gray3 : theme.colors.gray2};
      border: 1px solid ${$isFeatured ? theme.colors.gray7 : theme.colors.gray6};
      border-radius: 12px;
      min-height: 200px;
      width: 100%;
      text-align: center;
      transition: all 0.2s ease;
      position: relative;

      ${$isFeatured &&
      css`
        &::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, ${theme.colors.blue9}, ${theme.colors.blue10});
          border-radius: 12px 12px 0 0;
        }
      `}

      &:hover {
        border-color: ${theme.colors.gray7};
        background: ${theme.colors.gray3};
      }

      @media (max-width: ${theme.breakpoints.md}px) {
        width: 100%;
        min-height: 160px;
        padding: 24px 20px;
      }
    `;
  }}
`;

const LogoAndAvatarContainer = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      margin-bottom: 20px;
    `;
  }}
`;

const LogoImage = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      justify-content: center;

      img {
        height: 32px;
        width: auto;
        object-fit: contain;
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        img {
          height: 40px;
        }
      }
    `;
  }}
`;

const AvatarImage = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      justify-content: center;

      img {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid ${theme.colors.gray8};
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        img {
          width: 60px;
          height: 60px;
        }
      }
    `;
  }}
`;

const Description = styled.p`
  ${({ theme }) => {
    return css`
      all: unset;
      color: ${theme.colors.gray11};
      ${typography.textLgLight};
      line-height: 1.6;
      margin-bottom: 16px;
    `;
  }}
`;

const AuthorInfo = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      margin-top: auto;
    `;
  }}
`;

const AuthorName = styled.p`
  ${({ theme }) => {
    return css`
      all: unset;
      color: ${theme.colors.gray12};
      ${typography.textBaseLight};
      font-weight: 600;
    `;
  }}
`;

const AuthorRole = styled.p`
  ${({ theme }) => {
    return css`
      all: unset;
      color: ${theme.colors.gray10};
      ${typography.textSmLight};
      font-weight: 400;
    `;
  }}
`;

const NavigationButtons = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      justify-content: center;
      gap: 12px;
      margin-top: 24px;

      @media (max-width: ${theme.breakpoints.sm}px) {
        gap: 8px;
        margin-top: 20px;
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        gap: 16px;
        margin-top: 32px;
      }

      @media (max-width: ${theme.breakpoints.md}px) {
        display: none;
      }
    `;
  }}
`;

const NavButton = styled.button`
  ${({ theme }) => {
    return css`
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      background: ${theme.colors.gray3};
      border: 1px solid ${theme.colors.gray6};
      border-radius: 8px;
      cursor: pointer;
      color: ${theme.colors.gray11};
      transition: all 0.2s ease;

      @media (max-width: ${theme.breakpoints.sm}px) {
        width: 40px;
        height: 40px;
      }

      @media (min-width: ${theme.breakpoints.md}px) {
        width: 48px;
        height: 48px;
      }

      &:hover {
        background: ${theme.colors.gray4};
        border-color: ${theme.colors.gray7};
        color: ${theme.colors.gray12};
      }
    `;
  }}
`;
